import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, GripVertical, Plus, Image as ImageIcon, Tag, RefreshCw, Zap, Settings2, AlertTriangle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Badge } from '../ui/badge';
import api from '../../lib/api';

/**
 * PromotionCardsGridEditor - Éditeur CMS pour le bloc Grille de Cartes Promotionnelles
 * 
 * VERSION: 5.0 - PRODUCTION READY (Clean)
 * 
 * MODES SUPPORTÉS:
 * ✅ 'offers_auto' - Affiche AUTOMATIQUEMENT toutes les offres actives (RECOMMANDÉ)
 * ✅ 'manual'      - Création manuelle de cartes statiques
 * 
 * ARCHITECTURE:
 * - Pas de fallback implicite
 * - Validation stricte des modes
 * - Migration explicite si mode legacy détecté
 */

// Modes CMS valides - Liste exhaustive
const VALID_MODES = ['manual', 'offers_auto'];

// Modes legacy qui nécessitent une migration
const LEGACY_MODES = ['offers', 'selection'];

const PromotionCardsGridEditor = ({ content, onChange }) => {
  const { 
    cards = [], 
    corner_radius = 'lg',
    card_spacing = 'md',
    source_mode = 'manual',
    offers_limit = 20,
    offers_partner_id = null
  } = content;

  // Détecter si le mode nécessite une migration
  const isLegacyMode = LEGACY_MODES.includes(source_mode);
  const currentMode = isLegacyMode ? 'offers_auto' : source_mode;
  const isValidMode = VALID_MODES.includes(currentMode);

  const [visualMode, setVisualMode] = useState('url');
  const [offers, setOffers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [showMigrationBanner, setShowMigrationBanner] = useState(isLegacyMode);

  // Charger les offres actives (pour preview)
  const loadOffers = async () => {
    setLoadingOffers(true);
    try {
      const response = await api.get('/offers/public/active?limit=100');
      setOffers(response.data || []);
    } catch (error) {
      console.error('[CMS Editor] Erreur chargement offres:', error);
    } finally {
      setLoadingOffers(false);
    }
  };

  // Charger les partenaires pour le filtre
  const loadPartners = async () => {
    setLoadingPartners(true);
    try {
      const response = await api.get('/partners?is_active=true');
      // Tri alphabétique A→Z
      const sortedPartners = (response.data || [])
        .slice()
        .sort((a, b) => {
          const nameA = (a.nom || a.name || '').toLowerCase().trim();
          const nameB = (b.nom || b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('[CMS Editor] Erreur chargement partenaires:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  useEffect(() => {
    if (currentMode === 'offers_auto') {
      loadOffers();
      loadPartners();
    }
  }, [currentMode]);

  // Migrer explicitement le mode legacy
  const handleMigrateLegacy = () => {
    onChange({
      ...content,
      source_mode: 'offers_auto'
    });
    setShowMigrationBanner(false);
  };

  // Générer slug depuis titre
  const generateSlugFromTitle = (title) => {
    if (!title || !title.trim()) return '';
    return title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Ajouter une nouvelle carte
  const handleAddCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      image: '',
      title: '',
      description: '',
      slug: '',
      link: '',
      order: cards.length,
      is_active: true
    };
    onChange({
      ...content,
      cards: [...cards, newCard]
    });
  };

  // Supprimer une carte
  const handleDeleteCard = (cardId) => {
    onChange({
      ...content,
      cards: cards.filter(c => c.id !== cardId)
    });
  };

  // Mettre à jour une carte
  const handleUpdateCard = (cardId, field, value) => {
    onChange({
      ...content,
      cards: cards.map(c => {
        if (c.id !== cardId) return c;
        const updated = { ...c, [field]: value };
        if (field === 'title') {
          const newSlug = generateSlugFromTitle(value);
          updated.slug = newSlug;
          updated.link = newSlug ? `/${newSlug}` : '';
        }
        return updated;
      })
    });
  };

  // Réorganiser les cartes (drag & drop)
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(cards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const updatedCards = items.map((item, index) => ({ ...item, order: index }));
    onChange({
      ...content,
      cards: updatedCards
    });
  };

  // Upload d'image
  const handleImageUpload = async (cardId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.url) {
        handleUpdateCard(cardId, 'image', data.url);
      }
    } catch (error) {
      console.error('[CMS Editor] Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    }
  };

  // Changer le mode source
  const handleSourceModeChange = (mode) => {
    if (!VALID_MODES.includes(mode)) {
      console.error(`[CMS Editor] Mode invalide: ${mode}`);
      return;
    }
    onChange({
      ...content,
      source_mode: mode
    });
    if (mode === 'offers_auto') {
      loadOffers();
      loadPartners();
    }
  };

  // Mettre à jour les paramètres offers_auto
  const handleOffersAutoConfig = (field, value) => {
    onChange({
      ...content,
      [field]: value
    });
  };

  // Compter les offres filtrées pour preview
  const getFilteredOffersCount = () => {
    let filtered = offers;
    if (offers_partner_id) {
      filtered = filtered.filter(o => o.partner_id === offers_partner_id);
    }
    return Math.min(filtered.length, offers_limit || 20);
  };

  return (
    <div className="space-y-6">
      {/* Bannière de migration si mode legacy détecté */}
      {showMigrationBanner && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-800">
                Mode CMS obsolète détecté: "{source_mode}"
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Ce mode n'est plus supporté. Cliquez sur "Migrer" pour utiliser le mode automatique.
              </p>
              <div className="flex gap-2 mt-3">
                <Button 
                  size="sm" 
                  onClick={handleMigrateLegacy}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Migrer vers mode automatique
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowMigrationBanner(false)}
                >
                  Ignorer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Erreur si mode invalide */}
      {!isValidMode && !isLegacyMode && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800">
                Mode CMS non reconnu: "{source_mode}"
              </h4>
              <p className="text-sm text-red-600 mt-1">
                Modes valides: <code className="bg-red-100 px-1 rounded">manual</code>, <code className="bg-red-100 px-1 rounded">offers_auto</code>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MODE SELECTION - 2 modes uniquement */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <Tag className="h-5 w-5" />
          Source des promotions
        </h4>
        <p className="text-sm text-amber-700 mb-4">
          Choisissez comment alimenter cette grille
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Mode Offres Auto - RECOMMANDÉ */}
          <label 
            className={`relative flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
              currentMode === 'offers_auto' 
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <input
              type="radio"
              name="source_mode"
              value="offers_auto"
              checked={currentMode === 'offers_auto'}
              onChange={() => handleSourceModeChange('offers_auto')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <Zap className={`h-5 w-5 ${currentMode === 'offers_auto' ? 'text-primary' : 'text-gray-400'}`} />
              <span className="font-medium text-sm">Automatique</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Toutes les offres actives s'affichent automatiquement
            </p>
            <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs">
              Recommandé
            </Badge>
          </label>

          {/* Mode Manuel */}
          <label 
            className={`flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
              currentMode === 'manual' 
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <input
              type="radio"
              name="source_mode"
              value="manual"
              checked={currentMode === 'manual'}
              onChange={() => handleSourceModeChange('manual')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <Settings2 className={`h-5 w-5 ${currentMode === 'manual' ? 'text-primary' : 'text-gray-400'}`} />
              <span className="font-medium text-sm">Manuel</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Créer des cartes personnalisées
            </p>
          </label>
        </div>
      </div>

      {/* ========== MODE OFFERS_AUTO ========== */}
      {currentMode === 'offers_auto' && (
        <div className="space-y-4">
          {/* Configuration */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Configuration (optionnel)
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Limite */}
              <div className="space-y-2">
                <Label htmlFor="offers-limit">Nombre maximum d'offres</Label>
                <Input
                  id="offers-limit"
                  type="number"
                  min="1"
                  max="50"
                  value={offers_limit || 20}
                  onChange={(e) => handleOffersAutoConfig('offers_limit', parseInt(e.target.value) || 20)}
                  className="bg-white"
                />
                <p className="text-xs text-muted-foreground">De 1 à 50 offres</p>
              </div>

              {/* Filtre partenaire */}
              <div className="space-y-2">
                <Label htmlFor="offers-partner">Filtrer par partenaire</Label>
                <Select
                  value={offers_partner_id || 'all'}
                  onValueChange={(value) => handleOffersAutoConfig('offers_partner_id', value === 'all' ? null : value)}
                >
                  <SelectTrigger id="offers-partner" className="bg-white">
                    <SelectValue placeholder="Tous les partenaires" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les partenaires</SelectItem>
                    {partners.map(partner => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Laisser vide pour afficher toutes les offres
                </p>
              </div>
            </div>
          </div>

          {/* Preview des offres */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Aperçu ({getFilteredOffersCount()} offres)
              </h4>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={loadOffers}
                disabled={loadingOffers}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingOffers ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            {loadingOffers ? (
              <div className="text-center py-6 text-muted-foreground">
                Chargement des offres...
              </div>
            ) : offers.length === 0 ? (
              <div className="text-center py-6">
                <Tag className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                <p className="text-muted-foreground">Aucune offre active</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Les offres créées par Admin/Partenaires apparaîtront ici automatiquement
                </p>
              </div>
            ) : (
              <div className="grid gap-2 max-h-60 overflow-y-auto">
                {offers
                  .filter(o => !offers_partner_id || o.partner_id === offers_partner_id)
                  .slice(0, offers_limit || 20)
                  .map(offer => (
                    <div
                      key={offer.id}
                      className="flex items-center gap-3 p-2 bg-white rounded border border-gray-200"
                    >
                      {offer.image_url && (
                        <img 
                          src={offer.image_url} 
                          alt=""
                          className="w-10 h-10 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{offer.title}</p>
                        <p className="text-xs text-muted-foreground">{offer.partner_name}</p>
                      </div>
                      {offer.badge_text && (
                        <Badge 
                          style={{ backgroundColor: offer.badge_color || '#ef4444' }}
                          className="text-white text-xs"
                        >
                          {offer.badge_text}
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Info box */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
            <p className="font-semibold mb-1">✅ Mode Automatique activé</p>
            <p>Les offres créées par les Admins et Partenaires s'afficheront automatiquement sur cette page, sans intervention manuelle.</p>
          </div>
        </div>
      )}

      {/* ========== MODE MANUAL ========== */}
      {currentMode === 'manual' && (
        <>
          {/* Configuration globale */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3">Configuration de la Grille</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="corner-radius">Coins arrondis</Label>
                <Select
                  value={corner_radius}
                  onValueChange={(value) => onChange({ ...content, corner_radius: value })}
                >
                  <SelectTrigger id="corner-radius">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Petit (4px)</SelectItem>
                    <SelectItem value="md">Moyen (8px)</SelectItem>
                    <SelectItem value="lg">Grand (12px)</SelectItem>
                    <SelectItem value="xl">Très grand (16px)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="card-spacing">Espacement entre cartes</Label>
                <Select
                  value={card_spacing}
                  onValueChange={(value) => onChange({ ...content, card_spacing: value })}
                >
                  <SelectTrigger id="card-spacing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Serré (12px)</SelectItem>
                    <SelectItem value="md">Normal (24px)</SelectItem>
                    <SelectItem value="lg">Large (32px)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Liste des cartes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Cartes Promotionnelles ({cards.length})</h4>
              <Button
                type="button"
                size="sm"
                onClick={handleAddCard}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Ajouter une carte
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              📐 Les cartes s'affichent par 3 sur desktop, 1 sur mobile
            </p>

            {cards.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Aucune carte. Cliquez sur "Ajouter" pour commencer.</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="promotion-cards">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-3"
                    >
                      {cards.map((card, index) => (
                        <Draggable key={card.id} draggableId={card.id} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="bg-white border rounded-lg p-4"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-2 text-gray-400 hover:text-gray-600 cursor-grab"
                                >
                                  <GripVertical className="h-5 w-5" />
                                </div>

                                <div className="flex-1 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold text-gray-700">
                                        Carte #{index + 1}
                                      </span>
                                      {!card.is_active && (
                                        <Badge variant="secondary" className="text-xs">
                                          Inactive
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteCard(card.id)}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  <div className="space-y-3">
                                    {/* Image */}
                                    <div className="space-y-2">
                                      <Label>Image *</Label>
                                      <div className="flex gap-2 mb-2">
                                        <Button
                                          type="button"
                                          variant={visualMode === 'url' ? 'default' : 'outline'}
                                          size="sm"
                                          onClick={() => setVisualMode('url')}
                                        >
                                          URL
                                        </Button>
                                        <Button
                                          type="button"
                                          variant={visualMode === 'upload' ? 'default' : 'outline'}
                                          size="sm"
                                          onClick={() => setVisualMode('upload')}
                                        >
                                          Upload
                                        </Button>
                                      </div>
                                      
                                      {visualMode === 'url' ? (
                                        <Input
                                          type="url"
                                          value={card.image}
                                          onChange={(e) => handleUpdateCard(card.id, 'image', e.target.value)}
                                          placeholder="https://..."
                                        />
                                      ) : (
                                        <Input
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleImageUpload(card.id, e)}
                                        />
                                      )}

                                      {card.image && (
                                        <img
                                          src={card.image}
                                          alt="Prévisualisation"
                                          className="w-full h-32 object-cover rounded border"
                                        />
                                      )}
                                    </div>

                                    {/* Titre */}
                                    <div className="space-y-2">
                                      <Label>Titre *</Label>
                                      <Input
                                        type="text"
                                        value={card.title}
                                        onChange={(e) => handleUpdateCard(card.id, 'title', e.target.value)}
                                        placeholder="Titre de la carte"
                                      />
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-2">
                                      <Label>Description</Label>
                                      <Textarea
                                        value={card.description}
                                        onChange={(e) => handleUpdateCard(card.id, 'description', e.target.value)}
                                        placeholder="Description de la promotion..."
                                        rows={3}
                                      />
                                    </div>

                                    {/* Active */}
                                    <div className="space-y-2">
                                      <Label>Visibilité</Label>
                                      <div className="flex items-center gap-2 pt-1">
                                        <Switch
                                          checked={card.is_active}
                                          onCheckedChange={(checked) => handleUpdateCard(card.id, 'is_active', checked)}
                                        />
                                        <span className="text-sm text-muted-foreground">
                                          {card.is_active ? 'Visible' : 'Cachée'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Planification */}
                                    {card.is_active && (
                                      <div className="border-t pt-3 mt-3">
                                        <Label className="text-sm font-semibold mb-2 block">
                                          📅 Planification (optionnel)
                                        </Label>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                          <div className="space-y-2">
                                            <Label htmlFor={`start-${card.id}`} className="text-xs">
                                              Date de début
                                            </Label>
                                            <Input
                                              id={`start-${card.id}`}
                                              type="datetime-local"
                                              value={card.startDate || ''}
                                              onChange={(e) => handleUpdateCard(card.id, 'startDate', e.target.value)}
                                              className="text-sm"
                                            />
                                          </div>

                                          <div className="space-y-2">
                                            <Label htmlFor={`end-${card.id}`} className="text-xs">
                                              Date de fin
                                            </Label>
                                            <Input
                                              id={`end-${card.id}`}
                                              type="datetime-local"
                                              value={card.endDate || ''}
                                              onChange={(e) => handleUpdateCard(card.id, 'endDate', e.target.value)}
                                              className="text-sm"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </>
      )}

      {/* Aide contextuelle */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-semibold mb-2">💡 Conseils :</p>
        <ul className="list-disc list-inside space-y-1">
          {currentMode === 'offers_auto' ? (
            <>
              <li><strong>Mode Automatique :</strong> Les offres Admin/Partenaires s'affichent sans action manuelle</li>
              <li>Utilisez les filtres pour limiter le nombre ou cibler un partenaire</li>
              <li>Source unique de vérité : les modifications se font dans "Gestion des Offres"</li>
            </>
          ) : (
            <>
              <li>Images recommandées : 800x600px minimum (ratio 4:3)</li>
              <li>Les cartes s'affichent par 3 sur desktop, 1 sur mobile</li>
              <li>Utilisez le drag & drop pour réorganiser les cartes</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PromotionCardsGridEditor;
